'use client';

import { useEffect, useRef, useState } from 'react';

import classNames from 'classnames';

import { ChevronIcon } from '@/components';

import { ProductVariantCopy } from '@/lib/contentful';

import { getVariantTranslation } from '@/utils/getVariantTranslation';
import { TypeSubFlowV3Translations } from '@/utils/translations/TypeSubFlowV3Translations';

import { SubFlowV3ContextData } from '../../context/SubFlowV3Context';

interface LanguageSelectorProps {
  t?: TypeSubFlowV3Translations;
  data: SubFlowV3ContextData;
  onSelect: (language: string) => void;
  isPdpLangSelector?: boolean;
  productVariantsCopy?: ProductVariantCopy | null;
  dataTestId?: string;
}

const LanguageSelector = ({
  t,
  data,
  onSelect,
  isPdpLangSelector = false,
  productVariantsCopy,
  dataTestId = 'subflow-language-selector',
}: LanguageSelectorProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const node = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const handleClickOutside: EventListener = (event) => {
      if (node.current && !node.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    window.addEventListener('click', handleClickOutside);

    return () => {
      window.removeEventListener('click', handleClickOutside);
    };
  }, []);

  const LANGUAGE_MAPPINGS = {
    english: {
      label: productVariantsCopy
        ? getVariantTranslation(productVariantsCopy || null, 'British English')
        : t?.langEnglish || 'English',
      codes: ['en', 'en-GB', 'en-EU', 'en-AU', 'en-US'],
      primaryCode: 'en',
    },
    dutch: {
      label: productVariantsCopy
        ? getVariantTranslation(productVariantsCopy || null, 'Dutch')
        : t?.langDutch || 'Dutch',
      codes: ['nl'],
      primaryCode: 'nl',
    },
    german: {
      label: productVariantsCopy
        ? getVariantTranslation(productVariantsCopy || null, 'German')
        : t?.langGerman || 'German',
      codes: ['de'],
      primaryCode: 'de',
    },
    spanish: {
      label: productVariantsCopy
        ? getVariantTranslation(productVariantsCopy || null, 'Spanish')
        : t?.langSpanish || 'Spanish',
      codes: ['es'],
      primaryCode: 'es',
    },
    french: {
      label: productVariantsCopy
        ? getVariantTranslation(productVariantsCopy || null, 'French')
        : t?.langFrench || 'French',
      codes: ['fr'],
      primaryCode: 'fr',
    },
  };

  const SUPPORTED_LANGUAGES = Object.values(LANGUAGE_MAPPINGS).map((lang) => ({
    label: lang.label,
    code: lang.primaryCode,
    codes: lang.codes,
  }));

  const findLanguageByCode = (searchCode: string) =>
    SUPPORTED_LANGUAGES.find((lang) => lang.codes.includes(searchCode));

  const currentLanguageCode = data.language;

  const selected = findLanguageByCode(currentLanguageCode);

  const options = SUPPORTED_LANGUAGES.filter(
    (lang) => !lang.codes.includes(currentLanguageCode),
  );

  useEffect(() => {
    if (selected) {
      onSelect(selected.code);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!data.language) {
    return null;
  }

  return (
    <div className={classNames(isPdpLangSelector && 'w-full')}>
      <div
        className={classNames(
          !isPdpLangSelector ? 'mt-4 mb-6 md:mb-0' : 'mt-2 mb-1',
        )}
      >
        <div className="relative">
          <button
            ref={node}
            className="w-full border border-gray-400 px-4 py-3 text-left relative rounded-sm bg-white"
            onClick={() => setIsOpen((prev) => !prev)}
            data-testid={dataTestId}
          >
            {selected?.label}
            <ChevronIcon
              direction="down"
              classes="absolute right-3 top-1/2 transform -translate-y-1/2"
              color="grey-dark"
            />
          </button>

          {isOpen && (
            <div className="absolute left-0 top-[100%] z-10 w-full bg-white">
              {options?.map((lang) => (
                <button
                  className="block w-full border-b border-l border-r border-gray-400 px-4 py-3 text-left"
                  key={lang.code}
                  onClick={() => {
                    setIsOpen(false);
                    onSelect(lang.code);
                  }}
                >
                  {lang.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LanguageSelector;
